/**
 * Tela de login — TOTALMENTE NEUTRA (pedido explícito do dono): nada que
 * identifique a aplicação (sem nome, sem logo, sem domínio). Apenas um cadeado,
 * o campo de senha e o botão "Entrar". Erros: 401 = mensagem genérica; 429 =
 * mensagem com os segundos restantes (contador regressivo bloqueia o botão).
 */
import { useEffect, useRef, useState } from "react";
import { Button, Card, Center, PasswordInput, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconLock } from "@tabler/icons-react";
import { api, ErroApi, gravarToken } from "../api";

export function LoginPage({ onEntrar }: { onEntrar: () => void }) {
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState("");
  const [espera, setEspera] = useState(0); // segundos restantes do 429
  const [enviando, setEnviando] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  function iniciarContagem(segundos: number) {
    setEspera(segundos);
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => {
      setEspera((atual) => {
        if (atual <= 1 && timer.current) {
          clearInterval(timer.current);
          timer.current = null;
        }
        return Math.max(0, atual - 1);
      });
    }, 1000);
  }

  async function entrar() {
    if (!senha || espera > 0) return;
    setEnviando(true);
    setErro("");
    try {
      const r = await api.post<{ token: string }>("/login", { senha });
      gravarToken(r.token);
      onEntrar();
    } catch (e) {
      if (e instanceof ErroApi && e.status === 429) {
        const segundos = (e.corpo as { retryAfterSegundos?: number })?.retryAfterSegundos ?? 5;
        iniciarContagem(segundos);
      } else if (e instanceof ErroApi && e.status === 401) {
        setErro("Senha incorreta.");
      } else {
        setErro("Não foi possível entrar agora. Tente de novo.");
      }
      setSenha("");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Center style={{ minHeight: "100vh" }}>
      <Card withBorder shadow="sm" radius="md" w={320}>
        <Stack align="center" gap="md">
          <ThemeIcon size={56} radius="xl" variant="light" color="gray" aria-label="Área restrita">
            <IconLock size={30} />
          </ThemeIcon>
          <PasswordInput
            placeholder="Senha"
            aria-label="Senha"
            value={senha}
            onChange={(e) => setSenha(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void entrar();
            }}
            w="100%"
            autoFocus
          />
          {espera > 0 ? (
            <Text size="sm" c="red.7" role="alert">
              Muitas tentativas — aguarde {espera} segundo{espera === 1 ? "" : "s"}.
            </Text>
          ) : (
            erro && (
              <Text size="sm" c="red.7" role="alert">
                {erro}
              </Text>
            )
          )}
          <Button fullWidth onClick={() => void entrar()} loading={enviando} disabled={!senha || espera > 0}>
            Entrar
          </Button>
        </Stack>
      </Card>
    </Center>
  );
}
